// Mirrors the backend enum `MarketplaceDemandStatus` — these five values are the
// only ones the API can return. A finished demand is flagged via `isArchived`.
export type DemandStatus =
  | 'OFFERED'
  | 'INTERESTED_AVAILABLE'
  | 'PARTIALLY_COVERED'
  | 'COVERED'
  | 'NOT_MATCHABLE'

export type TrafficLightColor = 'green' | 'yellow' | 'red' | 'unknown'

export type SibInterestStatus = 'none' | 'interested' | 'rejected' | 'withdrawn' | 'confirmed'

export type RejectionReason = 'DISTANCE' | 'NO_CAPACITY' | 'NO_FIT' | 'OTHER' | 'NOT_SELECTED'

export interface Demand {
  id: string | number
  bankName?: string
  bankId?: number
  customerId?: number
  customerName?: string
  postal?: string
  city?: string
  country?: string
  salesRegion?: string
  serviceRecipient?: string
  serviceScope?: string
  periodStart?: string
  periodEnd?: string
  topic?: string
  description?: string
  status: DemandStatus | string
  visibilityMode?: string
  /** Present on manager views: user ids of a SPECIFIC_USERS visibility selection. */
  selectedUserIds?: string[]
  published: boolean
  /** Set once the demand is finished; locks it for any further user action. */
  isArchived?: boolean
  /**
   * SIB-924: authoritative edit-eligibility signal from the backend — whether
   * any expression of interest (also withdrawn/confirmed) ever existed.
   * Absent on older backends and on user-facing views.
   */
  hasInterestHistory?: boolean | null
  buckets?: Array<{
    serviceType: string
    requestedUnits: number
  }>
  createdAt?: string
  updatedAt?: string
  expiresAt?: string
  interestsCount?: number
  rejectedCount?: number
  remainingDemandBuckets?: Array<{
    serviceType: string
    remainingUnits: number
  }>

  allocationsCount?: number
  allocations?: Array<{
    userId: string
    userName: string
    allocationBuckets: Array<{
      serviceType: string
      allocatedUnits: number
    }>
  }>
}

export interface MatchingSib {
  workingArea?: string
  salesRegion?: string
  workRadius?: number
  id: string
  name: string
  postalCode?: string
  city?: string
  contry?: string
  trafficLightStatus?: TrafficLightColor
  trafficLightReason?: string
  selected?: boolean

  remainingCapacity?: Array<{
    serviceType: string
    units: number
  }>

  orderedUnits?: Array<{
    serviceType: string
    ordered: number
    completed: number
  }>

  topics: Array<{
    code: string
    nameDe: string
  }>
  missingTopics?: string[]
}

export interface MarketplaceDemand extends Demand {
  capacityFit?: 'green' | 'yellow' | 'red'
  sibInterestStatus?: SibInterestStatus
  allowedActions?: {
    canAccept: boolean
    canReject: boolean
    canWithdrawInterest: boolean
  }
  userInterestStatus?: 'NONE' | 'INTERESTED' | 'REJECTED' | 'WITHDRAWN' | 'CONFIRMED' | null
}

export interface InterestedSib {
  workingArea?: string
  id: string
  name: string
  postalCode?: string
  city?: string
  interestStatus: SibInterestStatus
  rejectionReason?: RejectionReason
  rejectionReasonText?: string
  confirmed?: boolean
  remainingCapacity?: Array<{
    serviceType: string
    units: number
  }>

  orderedUnits?: Array<{
    serviceType: string
    ordered: number
    completed: number
  }>

  topics?: string[]
  targetAchievement?: number
}

export interface DemandAssignment {
  sibId: string
  sibName: string
  units: Array<{
    serviceType: string
    assignedUnits: number
  }>
}

export interface CreateDemandPayload {
  bankId: number
  customerId: number
  visibilityMode: 'ALL_USERS' | 'SELECTED_USERS'
  selectedUserIds: string[]
  periodStart: string
  periodEnd: string
  postal?: string
  city?: string
  country?: string
  salesRegion?: string
  topic?: string
  description?: string
  buckets: Array<{
    serviceType: string
    requestedUnits: number
  }>
  periodRangeValid: boolean
  visibilitySelectionValid: boolean
}

export interface RejectDemandPayload {
  reason: RejectionReason
  details?: string
}

export interface ApiError {
  message: string
  fieldErrors?: Record<string, string>
}

export type MatchingSibsResponse = {
  matchedSibs: Array<{
    userId: string
    name: string
    postal: string | null
    city: string | null
    country: string | null
    salesRegion: string | null
    workRadius: number | null
    workingArea: string | null
    topics?: Array<{
      code: string
      nameDe: string
    }>
    trafficLightStatus: 'GREEN' | 'YELLOW' | 'RED'
    availableCapacity: number
    demandedUnits: number
    overview?: Array<{
      serviceCode: string
      capacityUnits: number
      orderedUnits: number
      doneUnits: number
      doneCapacityPct: number
      remainingCapacityPct: number
      remainingCapacity: number
      demandUnits: number
      remainingDemandUnits: number
    }>
  }>
}

export const TOPIC_OPTIONS = [
  { value: 'RETIREMENT_PROVISION', label: 'Altersvorsorge' },
  { value: 'VERMOEGEN_PLUS', label: 'VermögenPlus' },
  { value: 'MEIN_INVEST', label: 'MeinInvest' },
  { value: 'UNION_DEPOT_KOMFORT', label: 'UnionDepot Komfort' },
  { value: 'WERT_ANLAGE', label: 'WertAnlage' },
  { value: 'ELTIF', label: 'Eltif' },
  { value: 'WEALTH_STRUCTURING', label: 'Vermögensstrukturierung' },
  { value: 'CUSTOMER_SERVICE_CENTER', label: 'Begleitung im Kundenservicecenter' },
  { value: 'PREMIUM_SEGMENT', label: 'Begleitung im gehobenen Segment' },
  { value: 'BANKING_WORKSPACE', label: 'Banking Workspace' },
  { value: 'FUND_LINKED_PENSION', label: 'Fondsgebundene Rentenversicherung' },
]

export const TOPIC_LABELS = Object.fromEntries(
  TOPIC_OPTIONS.map(option => [option.value, option.label])
)

export function getTopicLabel(topic: string) {
  return TOPIC_LABELS[topic as keyof typeof TOPIC_LABELS] ?? topic
}

export type SibCapacityDetails = {
  workingArea?: string
  userId: string
  fullName: string
  postal?: string | null
  city?: string | null
  salesRegion?: string | null
  workRadius?: string | number | null
  topics?: string[]
  services?: Array<{
    serviceCode: string
    capacityUnits?: number
    orderedUnits?: number
    doneUnits?: number
    remainingCapacity?: number
  }>
}
